import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import EmptyState from "@/components/feature/EmptyState";
import ShareDialog from "@/components/feature/ShareDialog";
import SectionHeader from "@/components/feature/SectionHeader";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/site";
import { getMovieById, getSimilarMovies } from "@/utils/content";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id || "");
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie
      ? `${movie.title} 高清在线观看 - 秘密影院`
      : "影视详情 - 秘密影院",
    description: movie
      ? `${movie.title}是由${movie.director}执导的${movie.type}，${movie.year}年${movie.region}出品，主演${movie.actors.join("、")}。剧情简介：${movie.description.slice(0, 70)}……`
      : "秘密影院影视详情页",
    keywords: movie
      ? `${movie.title},${movie.title}在线观看,${movie.genres.join(",")},秘密影院`
      : "秘密影院,影视详情"
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  };

  if (!movie) {
    return (
      <div className="w-full px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="这部影视暂时不存在"
          description="你访问的作品可能已被移除或链接有误，去分类页看看其他精彩内容吧。"
          action={
            <Link
              to="/category/all"
              className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
            >
              返回全部影视
            </Link>
          }
        />
      </div>
    );
  }

  const category = categories.find((item) => item.type === movie.type);
  const similar = getSimilarMovies(movie, 4);

  const metaRows = [
    { label: "类型", value: movie.type },
    { label: "地区", value: movie.region },
    { label: "年份", value: String(movie.year) },
    { label: movie.episodes ? "集数" : "片长", value: movie.episodes ? `${movie.episodes} 集` : movie.duration },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "更新时间", value: movie.updatedAt }
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照背景`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/85 to-background-950/70" />
        </div>

        <div className="relative w-full px-4 pb-10 pt-10 md:px-6 md:pb-14 md:pt-14">
          <nav className="mb-6 flex flex-wrap items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-400">
              首页
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            {category && (
              <>
                <Link
                  to={`/category/${category.key}`}
                  className="cursor-pointer transition-colors hover:text-accent-400"
                >
                  {category.label}
                </Link>
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                </span>
              </>
            )}
            <span className="text-foreground-200">{movie.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-36 shrink-0 overflow-hidden rounded-lg border border-background-700 md:w-52">
              <img
                src={movie.cover}
                alt={`${movie.title} ${movie.type}海报`}
                title={`${movie.title} 高清在线观看`}
                className="aspect-[2/3] w-full object-cover object-top"
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-secondary-500/20 px-2.5 py-0.5 text-xs font-500 text-secondary-300">
                  {movie.type}
                </span>
                <span className="rounded-md bg-background-800 px-2.5 py-0.5 text-xs text-foreground-300">
                  {movie.region}
                </span>
                <span className="rounded-md bg-background-800 px-2.5 py-0.5 text-xs text-foreground-300">
                  {movie.year}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-3xl font-800 leading-tight text-foreground-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 text-sm italic text-accent-300">“{movie.tagline}”</p>

              <div className="mt-4 flex items-center gap-4">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-3xl font-800 text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-500">/ 10</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {movie.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/search?q=${encodeURIComponent(genre)}`}
                      className="cursor-pointer rounded-full border border-background-600 bg-background-900/70 px-3 py-0.5 text-xs text-foreground-300 transition-colors hover:border-secondary-500 hover:text-secondary-300"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => showToast("演示状态，正式播放功能敬请期待")}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-play-fill" aria-hidden="true" />
                  </span>
                  立即观看
                </button>
                <button
                  type="button"
                  onClick={() => showToast("已加入片单（演示功能）")}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-600 bg-background-900/70 px-5 py-2.5 text-sm font-600 text-foreground-100 transition-colors hover:border-secondary-500 hover:text-secondary-300"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-bookmark-line" aria-hidden="true" />
                  </span>
                  加入片单
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-600 bg-background-900/70 px-5 py-2.5 text-sm font-600 text-foreground-100 transition-colors hover:border-secondary-500 hover:text-secondary-300"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-share-forward-line" aria-hidden="true" />
                  </span>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 py-10 md:px-6 md:py-12">
        <div className="grid gap-10 lg:grid-cols-[1.8fr_1fr]">
          <div>
            <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-700 text-foreground-50">
              <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              剧情简介
            </h2>
            <p className="text-sm leading-7 text-foreground-300 md:text-base md:leading-8">
              {movie.description}
            </p>

            <div className="mt-8 flex items-start gap-3 rounded-lg border border-accent-500/30 bg-accent-500/10 p-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500/20 text-accent-400">
                <i className="ri-information-line text-lg" aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-heading text-sm font-600 text-accent-200">
                  观看区提示
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-300">
                  本站内容为影视展示与推荐演示，暂不提供在线播放与下载服务。资源正在更新中，敬请期待。
                </p>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="mb-4 flex items-center gap-3 font-heading text-xl font-700 text-foreground-50">
                <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                详细信息
              </h2>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg border border-background-800 bg-background-900/50 p-5 sm:grid-cols-2">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-foreground-200">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <aside>
            <div className="rounded-lg border border-background-800 bg-background-900/50 p-5">
              <h3 className="font-heading text-base font-600 text-foreground-50">
                相关信息
              </h3>
              <div className="mt-4 space-y-3 text-sm">
                <p className="flex items-center justify-between">
                  <span className="text-foreground-500">综合评分</span>
                  <span className="text-accent-400">{movie.rating.toFixed(1)} 分</span>
                </p>
                <p className="flex items-center justify-between">
                  <span className="text-foreground-500">热度指数</span>
                  <span className="text-foreground-200">{movie.popularity}</span>
                </p>
                <p className="flex items-center justify-between">
                  <span className="text-foreground-500">最近更新</span>
                  <span className="text-foreground-200">{movie.updatedAt}</span>
                </p>
              </div>
              {category && (
                <Link
                  to={`/category/${category.key}`}
                  className="mt-5 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-700 py-2 text-sm text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  查看更多「{category.label}」
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </span>
                </Link>
              )}
            </div>
          </aside>
        </div>

        {similar.length > 0 && (
          <section className="mt-14">
            <SectionHeader
              title="相关推荐"
              subtitle="根据类型与标签为你关联的同类型佳作"
            />
            <MovieGrid movies={similar} />
          </section>
        )}
      </div>

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={movie.title}
      />

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-md border border-background-700 bg-background-800 px-5 py-3 text-sm text-foreground-100 animate-scale-in lg:bottom-10">
          {toast}
        </div>
      )}
    </div>
  );
}